import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { Url } from 'next/dist/shared/lib/router/router'
import NextLink, { LinkProps } from 'next/link'
import { usePathname } from 'next/navigation'
import { NextRouter, useRouter } from 'next/router'
import { useRouter as useNprogressRouter } from 'next-nprogress-bar'
import { forwardRef, PropsWithChildren } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { ENABLE_I18N } from '@src/config/env'
import { TCountry, TAbsoluteLocale, LOCALE_FORMAT } from '@src/config/i18n.config'
import { parseFormat } from '@src/utils/general.utils'
import { parseLocale, getShortenedLocale, TRegionDetail, getAbsoluteLocale } from '@src/utils/i18n.utils'

const getLocalisedLinkInfo = ({
  currentPathname,
  href,
  locale,
  regionDetails,
  localeOverride,
}: {
  currentPathname: string
  href: Url
  locale: TAbsoluteLocale
  regionDetails: TRegionDetail
  localeOverride: string | false | undefined
}): {
  targetLocale: TAbsoluteLocale | TCountry | ''
  isRegionChange: boolean
  targetPathname: string | Url
} => {
  const { country } = parseLocale(locale)

  if (localeOverride === false) {
    return {
      targetLocale: '',
      isRegionChange: false,
      targetPathname: href,
    }
  }
  if (typeof localeOverride === 'string') {
    const isRegionChange =
      !!localeOverride && localeOverride !== country && parseFormat(LOCALE_FORMAT, localeOverride)?.country !== country
    return {
      targetLocale: getShortenedLocale(locale, regionDetails),
      isRegionChange,
      targetPathname: href,
    }
  }

  const { href: currentHrefWithoutLocale } = new URL(currentPathname, window.location.origin)

  const { pathname: targetPathname, search: targetSearch } = new URL(href as URL, currentHrefWithoutLocale)
  const targetPathnameParts = targetPathname.split('/')
  const potentialTargetLocale = targetPathnameParts[1]

  const isValidURLLocale =
    regionDetails.regions[potentialTargetLocale as TCountry] ??
    Object.values(regionDetails.regions).some(region =>
      Object.hasOwn(region.supportedLanguages, potentialTargetLocale as TAbsoluteLocale),
    )

  const urlLocale = isValidURLLocale && potentialTargetLocale ? potentialTargetLocale : ''

  if (urlLocale === '') {
    return {
      targetLocale: getShortenedLocale(locale, regionDetails),
      isRegionChange: false,
      targetPathname: targetPathname + targetSearch,
    }
  }
  if (urlLocale && urlLocale !== country && parseFormat(LOCALE_FORMAT, urlLocale)?.country !== country) {
    return {
      targetLocale: urlLocale as TAbsoluteLocale | TCountry,
      isRegionChange: true,
      targetPathname: `/${targetPathnameParts.slice(2).join('/')}${targetSearch}`,
    }
  } else {
    return {
      targetLocale: getShortenedLocale(locale, regionDetails),
      isRegionChange: false,
      targetPathname: `/${targetPathnameParts.slice(2).join('/')}${targetSearch}`,
    }
  }
}

export const Link = ENABLE_I18N
  ? forwardRef<HTMLAnchorElement, PropsWithChildren<LinkProps>>(function LinkI18N(
      { href, onClick, locale: localeOverride, ...restProps },
      ref,
    ) {
      const { locale, regionDetails, showConfirmationModal, setLocale } = useLocaleContext()
      const pathname = usePathname()
      const router = useRouter()
      const {
        targetLocale,
        isRegionChange = false,
        targetPathname,
      } = getLocalisedLinkInfo({
        locale,
        regionDetails,
        currentPathname: pathname,
        href,
        localeOverride,
      })

      return (
        <NextLink
          {...restProps}
          ref={ref}
          href={targetPathname}
          prefetch={false}
          data-prevent-nprogress={isRegionChange}
          onClick={e => {
            if (isRegionChange) {
              e.preventDefault()
              showConfirmationModal(parseLocale(getAbsoluteLocale(targetLocale, regionDetails)).country, () => {
                void setLocale(getAbsoluteLocale(targetLocale, regionDetails), true, false)
                void router.push(targetPathname, targetPathname, {
                  shallow: restProps.shallow,
                  scroll: restProps.scroll,
                  locale: targetLocale,
                })
                onClick?.(e)
              })
            } else {
              onClick?.(e)
            }
          }}
          locale={targetLocale}
        />
      )
    })
  : forwardRef<HTMLAnchorElement, PropsWithChildren<LinkProps>>(function LinkWithoutI18n({ href, ...restProps }, ref) {
      return <NextLink ref={ref} href={href} {...restProps} prefetch={false} />
    })

// @i18n-ignore
Link.displayName = 'CustomLink'

export const useQuinceNprogressRouter = ENABLE_I18N
  ? () => {
      const router = useNprogressRouter()
      const pathname = usePathname()
      const { locale, regionDetails, showConfirmationModal, setLocale } = useLocaleContext()

      const push = (to: string, options?: NavigateOptions & { locale?: string | false }) => {
        const {
          targetLocale,
          isRegionChange = false,
          targetPathname,
        } = getLocalisedLinkInfo({
          locale,
          regionDetails,
          currentPathname: pathname,
          href: to,
          localeOverride: options?.locale,
        })
        if (isRegionChange) {
          showConfirmationModal(parseLocale(getAbsoluteLocale(targetLocale, regionDetails)).country, () => {
            void setLocale(getAbsoluteLocale(targetLocale, regionDetails), true, false)
            router.push(targetPathname as string, {
              ...options,
              locale: targetLocale,
            })
          })
        } else {
          router.push(targetPathname as string, {
            ...options,
            locale: targetLocale,
          })
        }
      }

      const replace = (to: string, options?: NavigateOptions & { locale?: string | false }) => {
        const {
          targetLocale,
          isRegionChange = false,
          targetPathname,
        } = getLocalisedLinkInfo({
          locale,
          regionDetails,
          currentPathname: pathname,
          href: to,
          localeOverride: options?.locale,
        })
        if (isRegionChange) {
          showConfirmationModal(parseLocale(getAbsoluteLocale(targetLocale, regionDetails)).country, () => {
            void setLocale(getAbsoluteLocale(targetLocale, regionDetails), true, false)
            router.replace(targetPathname as string, {
              ...options,
              locale: targetLocale,
            })
          })
        } else {
          router.replace(targetPathname as string, {
            ...options,
            locale: targetLocale,
          })
        }
      }

      return {
        ...router,
        push,
        replace,
      }
    }
  : () => {
      const router = useNprogressRouter()
      const push = (to: string, options?: NavigateOptions) => {
        router.push(to, options)
      }

      const replace = (to: string, options?: NavigateOptions) => {
        router.replace(to, options)
      }

      return {
        ...router,
        push,
        replace,
      }
    }

export interface QuinceRouter extends Omit<NextRouter, 'push' | 'replace'> {
  push: (to: string, options?: { shallow?: boolean; scroll?: boolean; locale?: string | false }) => void
  replace: (to: string, as?: string, options?: { shallow?: boolean; scroll?: boolean; locale?: string | false }) => void
}

export const useQuinceRouter: () => QuinceRouter = ENABLE_I18N
  ? () => {
      const router = useRouter()
      const pathname = usePathname()
      const { locale, regionDetails, showConfirmationModal, setLocale } = useLocaleContext()

      const push = (to: string, options?: { shallow?: boolean; scroll?: boolean; locale?: string | false }) => {
        const {
          targetLocale,
          isRegionChange = false,
          targetPathname,
        } = getLocalisedLinkInfo({
          locale,
          regionDetails,
          currentPathname: pathname,
          href: to,
          localeOverride: options?.locale,
        })
        if (isRegionChange) {
          showConfirmationModal(parseLocale(getAbsoluteLocale(targetLocale, regionDetails)).country, () => {
            void setLocale(getAbsoluteLocale(targetLocale, regionDetails), true, false)
            void router.push(targetPathname, targetPathname, {
              ...options,
              locale: targetLocale,
            })
          })
        } else {
          void router.push(targetPathname, targetPathname, {
            ...options,
            locale: targetLocale,
          })
        }
      }

      const replace = (
        to: string,
        as?: string,
        options?: { shallow?: boolean; scroll?: boolean; locale?: string | false },
      ) => {
        const {
          targetLocale,
          isRegionChange = false,
          targetPathname,
        } = getLocalisedLinkInfo({
          locale,
          regionDetails,
          currentPathname: pathname,
          href: to,
          localeOverride: options?.locale,
        })
        if (isRegionChange) {
          showConfirmationModal(parseLocale(getAbsoluteLocale(targetLocale, regionDetails)).country, () => {
            void setLocale(getAbsoluteLocale(targetLocale, regionDetails), true, false)
            void router.replace(targetPathname, as ?? targetPathname, {
              ...options,
              locale: targetLocale,
            })
          })
        } else {
          void router.replace(targetPathname, as ?? targetPathname, {
            ...options,
            locale: targetLocale,
          })
        }
      }

      return {
        ...router,
        push,
        replace,
      }
    }
  : () => {
      const router = useRouter()

      const push = (to: string, options?: { shallow?: boolean; scroll?: boolean }) => {
        void router.push(to, to, options)
      }

      const replace = (to: string, as?: string, options?: { shallow?: boolean; scroll?: boolean }) => {
        void router.replace(to, as ?? to, options) // as is optional in next/router
      }

      return {
        ...router,
        push,
        replace,
      }
    }
